import { Link, type Href } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

// Building blocks shared by the per-city listing pages (city-events-screen.tsx,
// city-tours-screen.tsx) and the /events + /tours hubs' "by city" links. Same
// look as the wine-tour and boat-rental landing pages' own copies.

/** An <h2> (real heading element on web via role + aria-level). */
export function SectionHeading({ children }: { children: string }) {
  const theme = useTheme();
  return (
    <Text
      role="heading"
      aria-level={2}
      style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
      {children}
    </Text>
  );
}

export function CityChip({ label, active, href }: { label: string; active: boolean; href: Href }) {
  const theme = useTheme();
  return (
    // Pill styling on an inner View — see the same note in wine-tours-screen.tsx.
    <Link href={href} asChild>
      <Pressable accessibilityRole="link">
        {({ pressed }) => (
          <View
            style={[
              styles.chip,
              {
                backgroundColor: active ? theme.accent : theme.card,
                borderColor: active ? theme.accent : theme.border,
              },
              pressed && { opacity: 0.85 },
            ]}>
            <MapPin size={12} color={active ? theme.accentForeground : theme.mutedForeground} />
            <Text style={[styles.chipText, { color: active ? theme.accentForeground : theme.text }]}>
              {label}
            </Text>
          </View>
        )}
      </Pressable>
    </Link>
  );
}

/** A labelled row of CityChips — "Events in other Riviera towns". */
export function CityChipRow({
  heading,
  chips,
}: {
  heading: string;
  chips: { key: string; label: string; active: boolean; href: Href }[];
}) {
  const theme = useTheme();
  return (
    <View style={styles.chipBox}>
      <Text style={[styles.smallHeading, { color: theme.mutedForeground }]}>{heading}</Text>
      <View style={styles.chipsRow}>
        {chips.map((chip) => (
          <CityChip key={chip.key} label={chip.label} active={chip.active} href={chip.href} />
        ))}
      </View>
    </View>
  );
}

/** A non-link pill with a count — "Palais Nikaïa · 80". */
export function StatChip({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <View style={[styles.chip, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <MapPin size={12} color={theme.mutedForeground} />
      <Text style={[styles.chipText, { color: theme.text }]}>{label}</Text>
    </View>
  );
}

export function MoreLink({ label, href }: { label: string; href: Href }) {
  const theme = useTheme();
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" style={styles.moreLink}>
        {({ pressed }) => (
          <>
            <Text style={[styles.moreLinkText, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
              {label}
            </Text>
            <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
          </>
        )}
      </Pressable>
    </Link>
  );
}

export function Faq({ heading, entries }: { heading: string; entries: { q: string; a: string }[] }) {
  const theme = useTheme();
  if (entries.length === 0) return null;
  return (
    <View style={styles.section}>
      <SectionHeading>{heading}</SectionHeading>
      {entries.map((entry) => (
        <View key={entry.q} style={styles.faqItem}>
          <Text style={[styles.faqQuestion, { color: theme.text }]}>{entry.q}</Text>
          <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>{entry.a}</Text>
        </View>
      ))}
    </View>
  );
}

/** Rows shown per group before the "Show all" toggle. */
const VISIBLE_ROWS = 9;
/** Don't collapse a group to hide just a couple of rows behind a toggle. */
const MIN_HIDDEN_ROWS = 4;

/**
 * An <h3> group of compact text links, collapsed to its first few rows.
 *
 * Collapsed rows are hidden with `display: none`, not left unrendered: this
 * list is where most detail pages get their inbound link from, so every link
 * must stay in the static HTML — Google follows links in collapsed sections.
 * Same approach as BoatGroup in boat-rental-screen.tsx.
 */
export function LinkGroup({
  heading,
  count,
  showAllLabel,
  showFewerLabel,
  renderRow,
}: {
  heading: string;
  count: number;
  showAllLabel: string;
  showFewerLabel: string;
  renderRow: (index: number, hidden: boolean) => ReactNode;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const collapsible = count >= VISIBLE_ROWS + MIN_HIDDEN_ROWS;

  return (
    <View style={styles.group}>
      <Text role="heading" aria-level={3} style={[styles.groupHeading, { color: theme.text }]}>
        {`${heading} (${count})`}
      </Text>
      <View style={styles.rows}>
        {Array.from({ length: count }, (_, index) =>
          renderRow(index, collapsible && !open && index >= VISIBLE_ROWS),
        )}
      </View>
      {collapsible ? (
        <Pressable
          accessibilityRole="button"
          aria-expanded={open}
          onPress={() => setOpen((o) => !o)}
          style={styles.showAll}>
          {({ pressed }) => (
            <Text style={[styles.showAllText, { color: theme.gold }, pressed && { opacity: 0.6 }]}>
              {open ? showFewerLabel : showAllLabel}
            </Text>
          )}
        </Pressable>
      ) : null}
    </View>
  );
}

/** One compact row of a LinkGroup: the title as a link, then muted facts. */
export function LinkRow({
  href,
  title,
  facts,
  hidden,
}: {
  href: Href;
  title: string;
  facts: string;
  hidden: boolean;
}) {
  const theme = useTheme();
  return (
    <Link href={href} asChild>
      {/* Flattened: <Link asChild> rejects a style array on its child. */}
      <Pressable
        accessibilityRole="link"
        style={StyleSheet.flatten([styles.row, hidden && styles.hidden])}>
        {({ pressed }) => (
          <Text numberOfLines={1} style={[styles.rowText, pressed && { opacity: 0.6 }]}>
            <Text style={{ color: theme.primary }}>{title}</Text>
            {facts ? <Text style={{ color: theme.mutedForeground }}>{` · ${facts}`}</Text> : null}
          </Text>
        )}
      </Pressable>
    </Link>
  );
}

export const landingStyles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  section: {
    gap: Spacing.two,
    marginTop: Spacing.two,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  stats: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: -Spacing.two,
  },
  intro: {
    fontSize: 15,
    lineHeight: 23,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  moreLinks: {
    marginTop: Spacing.two,
    gap: Spacing.two,
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
});

const styles = StyleSheet.create({
  section: {
    gap: Spacing.two,
    marginTop: Spacing.two,
  },
  sectionHeading: {
    fontSize: 22,
    fontWeight: '700',
  },
  chipBox: {
    gap: Spacing.two,
  },
  smallHeading: {
    fontSize: 13,
    fontWeight: '600',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  moreLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  moreLinkText: {
    fontSize: 14,
    fontWeight: '600',
  },
  faqItem: {
    gap: 4,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '700',
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 21,
  },
  group: {
    gap: Spacing.one,
    marginTop: Spacing.two,
  },
  groupHeading: {
    fontSize: 16,
    fontWeight: '700',
  },
  rows: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Spacing.four,
  },
  row: {
    flexBasis: 320,
    flexGrow: 1,
    paddingVertical: 4,
  },
  rowText: {
    fontSize: 14,
  },
  hidden: {
    display: 'none',
  },
  showAll: {
    alignSelf: 'flex-start',
    paddingVertical: Spacing.two,
  },
  showAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
